import React, {
  useState,
  useMemo,
  useCallback,
  useEffect,
  useContext,
} from 'react';
import PropTypes from 'prop-types';
import { Spin, Table, Select, PageHeader, Row, Col, Button } from 'antd';
import moment from 'moment';
import { useHasPermission } from '@ohif/viewer/src/store/permission/hooks';
import * as api from './api';

const columns = [
  { title: '名称', dataIndex: ['agreement', 'title'] },
  { title: '签署时间', dataIndex: 'agreeTime' },
  { title: '状态', dataIndex: 'agreeStatus' },
];

const AgreementContext = React.createContext({ id: null, onChange: null });

const Action = (_, record) => {
  const { id, onChange } = useContext(AgreementContext);
  const { agreementId } = record;
  const handleRemoveBoundAgreement = useCallback(() => {
    api.removeBoundAgreement({ id, agreementId }).then(() => {
      onChange && onChange(record);
    });
  }, [agreementId, id, onChange, record]);
  return (
    <>
      <Button type="link" onClick={handleRemoveBoundAgreement}>
        解除
      </Button>
    </>
  );
};

const boundAgreementColumns = [
  { title: '名称', dataIndex: 'title' },
  {
    title: '绑定时间',
    dataIndex: 'createTime',
    render: text => moment(text).format('YYYY-MM-DD HH:mm:ss'),
  },
  { title: '操作', key: 'action', render: Action },
];

const Agreement = ({ id, disabled, onChange }) => {
  const [loading, setLoading] = useState(false);
  const [agreeList, setAgreeList] = useState([]);
  useEffect(() => {
    setLoading(true);
    api
      .queryAgreementState({ id })
      .then(res => setAgreeList(res.data || []))
      .finally(() => setLoading(false));
  }, [id]);

  const isMaster = useHasPermission({ action: 'MU0217', data: id });

  // 所有协议
  const [agreeOptions, setAgreeOptions] = useState([]);
  const agreementLabels = useMemo(
    () =>
      agreeOptions.reduce(
        (acc, item) => Object.assign(acc, { [item.id]: item.title }),
        {}
      ),
    [agreeOptions]
  );
  useEffect(() => {
    api.getAgreement().then(res => setAgreeOptions(res.data || []));
  }, []);

  // 绑定协议查看
  const [boundAgreement, setBoundAgreement] = useState([]);
  const handleGetBoundAgreement = useCallback(() => {
    api.getBoundAgreement({ id }).then(res => {
      const data = res.data || [];
      setBoundAgreement(
        data.map(item => ({
          ...item,
          title: agreementLabels[item.agreementId],
        }))
      );
    });
  }, [agreementLabels, id]);
  useEffect(handleGetBoundAgreement, [agreementLabels]);

  const boundAgreementAgreementId = useMemo(
    () => boundAgreement.map(item => item.agreementId),
    [boundAgreement]
  );

  // 绑定协议
  const [selectedAgree, setSelectedAgree] = useState();
  const handleCreateBindAgreement = useCallback(() => {
    if (!selectedAgree) return;
    api
      .createBindAgreement({
        agreementId: selectedAgree,
        imageScienceId: id,
      })
      .then(handleGetBoundAgreement);
  }, [handleGetBoundAgreement, id, selectedAgree]);

  return (
    <>
      <Spin spinning={loading}>
        <PageHeader title="协议" />
        <Table
          rowKey="accountId"
          columns={columns}
          dataSource={agreeList}
          pagination={false}
        />
      </Spin>
      {isMaster && (
        <>
          <PageHeader title="绑定协议" />
          <Row>
            <Col span={8}>
              <Select
                placeholder="选择绑定的协议"
                filterOption={false}
                onChange={setSelectedAgree}
              >
                {agreeOptions.map((item, index) => (
                  <Select.Option
                    key={index}
                    value={item.id}
                    label={item.title}
                    disabled={boundAgreementAgreementId.includes(item.id)}
                  >
                    {item.title}
                  </Select.Option>
                ))}
              </Select>
              <Button
                style={{ margin: '0 20px' }}
                type="primary"
                htmlType="submit"
                disabled={disabled}
                onClick={handleCreateBindAgreement}
              >
                绑定
              </Button>
            </Col>
            <Col span={16}>
              <AgreementContext.Provider
                value={{ id, onChange: handleGetBoundAgreement }}
              >
                <Table
                  rowKey="id"
                  columns={boundAgreementColumns}
                  dataSource={boundAgreement}
                  pagination={false}
                />
              </AgreementContext.Provider>
            </Col>
          </Row>
        </>
      )}
    </>
  );
};

Agreement.propTypes = {
  id: PropTypes.number,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
};

export default Agreement;
